import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "../../environments/environment";
import {
  RequestRefundTask,
  RequestWithdrawalTask,
  TaskRequest,
  TaskSummary,
  TaskType,
} from "@models/task-request";
import { Page } from "@models/page";
import { SnackBarService } from "./snack-bar.service";
import { PaginatorState } from "primeng/paginator";
import { Observable, catchError, of, tap, throwError } from "rxjs";

export const WITHDRAWAL_UNAVAILABLE_MESSAGE = "Saldo indisponível para saque";

export function withdrawalRequestErrorMessage(error: unknown): string {
  const httpError = error as HttpErrorResponse;
  const payload = httpError?.error;
  const raw = [
    payload?.error,
    payload?.message,
    Array.isArray(payload?.errors) ? payload.errors[0] : undefined,
  ].find((value) => typeof value === "string" && value.trim());

  if (raw) return String(raw);

  if (httpError?.status === 422 || httpError?.status === 500) {
    return WITHDRAWAL_UNAVAILABLE_MESSAGE;
  }

  return "Não foi possível solicitar o saque. Tente novamente.";
}

@Injectable({
  providedIn: "root",
})
export class AdminTasksService {
  private readonly baseUrl = `${environment.API_BACKEND}/admin_tasks`;

  constructor(
    private readonly http: HttpClient,
    private readonly notificationService: SnackBarService,
  ) {}

  getTasks(
    pageEvent?: PaginatorState | null,
    searchQuery?: string,
    status?: string | null,
    taskType?: string | null,
  ) {
    let params = new HttpParams()
      .set("page", (pageEvent?.page ?? 0) + 1)
      .set("per_page", pageEvent?.rows ?? 20);

    if (searchQuery) {
      params = params.set("search_query", searchQuery);
    }

    if (status) {
      params = params.set("status", status);
    }

    if (taskType) {
      params = params.set("task_type", taskType);
    }

    return this.http.get<Page<TaskRequest>>(this.baseUrl, { params }).pipe(
      catchError(() =>
        of({
          data: [],
          meta: {
            total_count: 0,
            total_pages: 0,
            current_page: 0,
            page_size: 20,
          },
        } as Page<TaskRequest>),
      ),
    );
  }

  getById(id: number): Observable<TaskRequest> {
    return this.http.get<TaskRequest>(`${this.baseUrl}/${id}`);
  }

  create(task: Partial<TaskRequest>): Observable<TaskRequest> {
    return this.http.post<TaskRequest>(this.baseUrl, {
      admin_task: task,
    });
  }

  update(id: number, task: Partial<TaskRequest>): Observable<TaskRequest> {
    return this.http.put<TaskRequest>(`${this.baseUrl}/${id}`, {
      admin_task: task,
    });
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  processRefund(id: number): Observable<TaskRequest> {
    return this.http.post<TaskRequest>(`${this.baseUrl}/${id}/refund`, {});
  }

  processWithdrawal(id: number): Observable<TaskRequest> {
    return this.http.post<TaskRequest>(`${this.baseUrl}/${id}/withdrawl`, {});
  }

  getSummary(): Observable<TaskSummary> {
    return this.http.get<TaskSummary>(`${this.baseUrl}/summary`).pipe(
      catchError(() =>
        of({
          approved: 0,
          rejected: 0,
          pending: 0,
        } as TaskSummary),
      ),
    );
  }

  requestWithdrawal(
    amount: number,
    invoiceUrl?: string,
    invoiceTitle = "Nota fiscal",
  ) {
    const admin_task: RequestWithdrawalTask = {
      title: "Solicitação de saque",
      task_type: TaskType.WITHDRAWAL,
      description: "Solicitação de saque",
      amount,
      ...(invoiceUrl
        ? { invoice_url: invoiceUrl, invoice_title: invoiceTitle }
        : {}),
    };
    return this.http
      .post<RequestWithdrawalTask>(
        `${environment.API_BACKEND}/v2/admin_tasks`,
        { admin_task },
      )
      .pipe(
        tap(() =>
          this.notificationService.openSnackBar(
            "Saque solicitado com sucesso. Acompanhe o status na página de solicitações.",
            "Fechar",
            "success",
          ),
        ),
        catchError((error: HttpErrorResponse) => {
          this.notificationService.openSnackBar(
            withdrawalRequestErrorMessage(error),
            "Fechar",
            "error",
          );
          return throwError(() => error);
        }),
      );
  }

  requestRefund(
    orderId: number,
    amount: number,
    description: string,
    customerReason: string,
  ) {
    const admin_task: RequestRefundTask = {
      title: `Reembolso - Pedido #${orderId}`,
      task_type: TaskType.REFUND,
      description,
      amount,
      order_id: orderId,
      customer_reason: customerReason,
    };
    return this.http
      .post<RequestRefundTask>(`${environment.API_BACKEND}/v3/admin_tasks`, {
        admin_task,
      })
      .pipe(
        tap(() =>
          this.notificationService.openSnackBar(
            "Solicitação de reembolso enviada com sucesso. Você será avisado assim que ela for analisada.",
            "Fechar",
            "success",
          ),
        ),
      );
  }
}
